import {
  DecisionPage,
  DecisionState,
  HomeCalendarEntry
} from "../../../models/home";
import { CalendarDateOperations } from "./calendar-date.operations";

const STATE_ORDER: Record<DecisionState, number> = {
  act_now: 0,
  act_soon: 1,
  waiting: 2,
  blocked: 2,
  in_review: 2,
  act_without_rush: 3,
  safe_to_ignore: 3,
  completed: 4,
  dismissed: 4
};

export interface CalendarEntryGroups {
  datedEntries: readonly HomeCalendarEntry[];
  undatedEntries: readonly HomeCalendarEntry[];
}

export class CalendarEntryMapper {
  static toEntries(items: readonly DecisionPage["items"][number][]): CalendarEntryGroups {
    const datedEntries: HomeCalendarEntry[] = [];
    const undatedEntries: HomeCalendarEntry[] = [];

    items.forEach((item) => {
      const dueAt = item.dueAt ?? "";
      if (!dueAt) {
        undatedEntries.push(CalendarEntryMapper.toEntry(item, CalendarDateOperations.toDateKey(new Date())));
        return;
      }

      const dayKey = CalendarDateOperations.toDateKey(dueAt);
      if (!dayKey) {
        undatedEntries.push(CalendarEntryMapper.toEntry(item, CalendarDateOperations.toDateKey(new Date())));
        return;
      }

      datedEntries.push(CalendarEntryMapper.toEntry(item, dayKey));
    });

    return {
      datedEntries: datedEntries.sort(CalendarEntryMapper.compareEntries),
      undatedEntries: undatedEntries.sort(CalendarEntryMapper.compareEntries)
    };
  }

  static groupByDay(entries: readonly HomeCalendarEntry[]): Map<string, readonly HomeCalendarEntry[]> {
    const grouped = new Map<string, HomeCalendarEntry[]>();

    entries.forEach((entry) => {
      const bucket = grouped.get(entry.dayKey) ?? [];
      bucket.push(entry);
      grouped.set(entry.dayKey, bucket);
    });

    return new Map(Array.from(grouped.entries()));
  }

  private static toEntry(
    item: DecisionPage["items"][number],
    dayKey: string
  ): HomeCalendarEntry {
    return {
      decisionId: item.decisionId,
      title: item.title,
      shortReason: item.shortReason,
      state: item.state,
      dueAt: item.dueAt ?? "",
      dayKey,
      dueDate: CalendarDateOperations.parseDateKey(dayKey),
      priorityScore: item.priority.score,
      priorityTier: item.priority.tier,
      ownerLabel: item.owner?.displayName
    };
  }

  private static compareEntries(left: HomeCalendarEntry, right: HomeCalendarEntry): number {
    const stateDelta = STATE_ORDER[left.state] - STATE_ORDER[right.state];
    if (stateDelta !== 0) {
      return stateDelta;
    }

    const priorityDelta = right.priorityScore - left.priorityScore;
    if (priorityDelta !== 0) {
      return priorityDelta;
    }

    return left.title.localeCompare(right.title);
  }
}
